import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { gsap } from 'gsap';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { FullMediaSlide } from '@/types';
import { getUploadUrl } from '@/config/api';
import ImageWithSkeleton from '@/components/ImageWithSkeleton';
import TextOverlay from './TextOverlay';
import { useCursorContext } from '@/providers/CursorProvider';
import RotateDeviceIndicator from '@/components/RotateDeviceIndicator';

interface FullMediaSectionProps {
    slide: FullMediaSlide;
    isActive?: boolean;
    onZoomChange?: (zoomed: boolean) => void;
}

const FullMediaSection: React.FC<FullMediaSectionProps> = ({ slide, isActive, onZoomChange }) => {
    const mediaRef = useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isPortrait, setIsPortrait] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const imageWrapperRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<gsap.core.Tween | null>(null);
    const [isUserInteracting, setIsUserInteracting] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);
    const [isAnimatingReady, setIsAnimatingReady] = useState(false); // controls black screen gating
    const clickStartPos = useRef({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isInNavigationZone, setIsInNavigationZone] = useState(false);
    const { setCursorType } = useCursorContext();
    const dragTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleImageClick = (e: React.MouseEvent) => {
        if (slide.image_url && !isDragging) {
            if (!isMobile && isInNavigationZone) return;
            window.open(slide.image_url, '_blank', 'noopener,noreferrer');
        }
    };

    const centerImage = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        el.scrollLeft = maxScroll > 0 ? Math.floor(maxScroll / 2) : 0;
    }, []);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsPortrait(window.matchMedia('(orientation: portrait)').matches);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        window.addEventListener('orientationchange', checkMobile);
        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('orientationchange', checkMobile);
        };
    }, []);

    useEffect(() => {
        if (mediaRef.current) {
            gsap.set(mediaRef.current, { y: 0, opacity: 1 });
        }
    }, [isActive, isMobile]);

    // Center image only for non-animated mobile images to avoid conflicting with auto-scroll start
    useEffect(() => {
        if (!isMobile || !isActive || slide.media_type !== 'image' || slide.enable_mobile_animation) return;
        centerImage();
        const t1 = setTimeout(centerImage, 0);
        const t2 = setTimeout(centerImage, 300);
        const onResize = () => centerImage();
        window.addEventListener('resize', onResize);
        const el = scrollContainerRef.current;
        const img: HTMLImageElement | null = el ? el.querySelector('img') : null;
        let onImgLoad: (() => void) | null = null;
        if (img && !img.complete) {
            onImgLoad = () => centerImage();
            img.addEventListener('load', onImgLoad);
        }
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            window.removeEventListener('resize', onResize);
            if (img && onImgLoad) img.removeEventListener('load', onImgLoad);
        };
    }, [centerImage, isMobile, isActive, slide.media_type, slide.media?.url]);

    useEffect(() => {
        if (isActive && isMobile && slide.media_type === 'image') {
            onZoomChange?.(isZoomed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, isMobile, slide.media_type, isZoomed]);

    // Handle zoom state tracking
    const handleTransform = useCallback((ref: ReactZoomPanPinchRef, state: { scale: number; positionX: number; positionY: number }) => {
        const zoomed = state.scale > 1.1;
        setIsZoomed(zoomed);
        onZoomChange?.(zoomed);
    }, [onZoomChange]);

    // Auto-scroll animation for mobile images with gating - ONLY in portrait mode
    useEffect(() => {
        const scroller = scrollContainerRef.current;
        if (!scroller || !isMobile || !isPortrait || !isActive || slide.media_type !== 'image' || isUserInteracting || isZoomed || !slide.enable_mobile_animation) {
            // Clean up animation if conditions not met
            if (animationRef.current) {
                animationRef.current.kill();
                animationRef.current = null;
            }
            setIsAnimatingReady(false);
            return;
        }

        const startAnimation = () => {
            if (animationRef.current) {
                animationRef.current.kill();
            }
            // Wait until scroll width stabilizes (image decoded & wrapper laid out) to avoid jump
            let stableFrames = 0;
            let lastWidth = scroller.scrollWidth;
            const ensureStable = () => {
                const currentWidth = scroller.scrollWidth;
                if (currentWidth === lastWidth) {
                    stableFrames += 1;
                } else {
                    stableFrames = 0;
                    lastWidth = currentWidth;
                }
                if (stableFrames < 2) { // two consecutive frames stable
                    requestAnimationFrame(ensureStable);
                    return;
                }
                const maxScroll = scroller.scrollWidth - scroller.clientWidth;
                if (maxScroll <= 0) {
                    setIsAnimatingReady(true); // nothing to scroll; just show
                    return;
                }
                const startPoint = Math.min(1, Math.max(0, slide.mobile_animation_start_point ?? 0));
                const startScrollLeft = Math.round(startPoint * maxScroll);
                scroller.scrollLeft = startScrollLeft; // deterministic start
                scroller.style.willChange = 'scroll-position';
                const duration = maxScroll / 25; // speed factor (25 = 2x slower than original 50)
                setIsAnimatingReady(true);
                // Fade in image while starting scroll
                if (imageWrapperRef.current) {
                    gsap.fromTo(imageWrapperRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power1.out' });
                }
                // If start point is in the middle, animate right first then yoyo left, else start from left
                if (startScrollLeft > 0 && startScrollLeft < maxScroll) {
                    // Go right to end, then yoyo back left indefinitely
                    animationRef.current = gsap.fromTo(
                        scroller,
                        { scrollLeft: startScrollLeft },
                        {
                            scrollLeft: maxScroll,
                            duration: duration * (1 - startPoint),
                            ease: 'none',
                            onComplete: () => {
                                animationRef.current = gsap.fromTo(
                                    scroller,
                                    { scrollLeft: maxScroll },
                                    {
                                        scrollLeft: 0,
                                        duration,
                                        ease: 'none',
                                        repeat: -1,
                                        yoyo: true,
                                        immediateRender: false,
                                        overwrite: true
                                    }
                                );
                            }
                        }
                    );
                } else {
                    animationRef.current = gsap.fromTo(
                        scroller,
                        { scrollLeft: startScrollLeft },
                        {
                            scrollLeft: maxScroll,
                            duration,
                            ease: 'none',
                            repeat: -1,
                            yoyo: true,
                            immediateRender: false,
                            overwrite: true
                        }
                    );
                }
            };
            requestAnimationFrame(ensureStable);
        };

        // Start animation after image loads
        const img: HTMLImageElement | null = scroller.querySelector('img');
        if (img && !img.complete) {
            img.addEventListener('load', startAnimation, { once: true });
        } else {
            startAnimation();
        }

        return () => {
            if (animationRef.current) {
                animationRef.current.kill();
                animationRef.current = null;
            }
        };
    }, [isMobile, isPortrait, isActive, slide.media_type, slide.media?.url, isUserInteracting, isZoomed, slide.enable_mobile_animation, slide.mobile_animation_start_point]);

    useEffect(() => {
        const strip = stripRef.current;
        const scroller = scrollContainerRef.current;
        if (!strip || !scroller || !isMobile || slide.media_type !== 'image') return;

        let dragging = false;
        let startX = 0;
        let startY = 0;
        let startScrollLeft = 0;
        let hasMoved = false;
        const SPEED = 4; // Move image 4x compared to finger travel
        const MOVE_THRESHOLD = 10; // px - movement threshold to determine intent

        const stopAll = (e: TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof (e as any).stopImmediatePropagation === 'function') {
                (e as any).stopImmediatePropagation();
            }
        };

        const onStart = (e: TouchEvent) => {
            dragging = true;
            hasMoved = false;
            setIsUserInteracting(true);
            setIsDragging(false);
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            startScrollLeft = scroller.scrollLeft;
        };

        const onMove = (e: TouchEvent) => {
            if (!dragging) return;

            const dx = e.touches[0].clientX - startX;
            const dy = e.touches[0].clientY - startY;
            const absX = Math.abs(dx);
            const absY = Math.abs(dy);

            // Once we detect movement, determine if it's horizontal or vertical
            if (!hasMoved && (absX > MOVE_THRESHOLD || absY > MOVE_THRESHOLD)) {
                hasMoved = true;

                // If movement is more vertical, don't interfere (let parent handle section navigation)
                if (absY > absX * 1.5) {
                    dragging = false;
                    setIsUserInteracting(false);
                    return;
                }
            }

            // Only scroll image if movement is clearly horizontal and image can scroll
            if (hasMoved && absX > absY && scroller.scrollWidth > scroller.clientWidth) {
                scroller.scrollLeft = startScrollLeft - dx * SPEED;
                setIsDragging(true);
                stopAll(e);
            }
        };

        const onEnd = (e: TouchEvent) => {
            dragging = false;
            hasMoved = false;
            setIsUserInteracting(false);
            // Only stop propagation if we actually handled the gesture
            const dragDistance = Math.abs(e.changedTouches[0].clientX - startX);
            if (dragDistance > MOVE_THRESHOLD) {
                stopAll(e);
            }
            // Reset isDragging immediately if no significant drag occurred
            // This ensures click events work on mobile
            if (dragDistance <= MOVE_THRESHOLD) {
                setIsDragging(false);
            } else {
                // If we dragged, reset after a delay to prevent accidental link opening
                if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
                dragTimeoutRef.current = setTimeout(() => setIsDragging(false), 100);
            }
        };

        strip.addEventListener('touchstart', onStart, { capture: true, passive: true } as AddEventListenerOptions);
        strip.addEventListener('touchmove', onMove, { capture: true, passive: false } as AddEventListenerOptions);
        strip.addEventListener('touchend', onEnd, { capture: true, passive: false } as AddEventListenerOptions);
        strip.addEventListener('touchcancel', onEnd, { capture: true, passive: false } as AddEventListenerOptions);

        return () => {
            strip.removeEventListener('touchstart', onStart, { capture: true } as AddEventListenerOptions);
            strip.removeEventListener('touchmove', onMove, { capture: true } as AddEventListenerOptions);
            strip.removeEventListener('touchend', onEnd, { capture: true } as AddEventListenerOptions);
            strip.removeEventListener('touchcancel', onEnd, { capture: true } as AddEventListenerOptions);
            if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
        };
    }, [isMobile, slide.media_type]);

    const renderMedia = () => {
        if (slide.media_type === 'video') {
            if (slide.is_external_video && slide.video_url) {
                const embedUrl = getEmbedUrl(slide.video_url, slide.video_platform);
                return (
                    <iframe
                        src={embedUrl}
                        title={slide.title}
                        style={{ width: '100%', height: '100%', border: 'none', objectFit: 'cover', pointerEvents: 'none' }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                );
            } else if (slide.media?.url) {
                return (
                    <video autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', backgroundColor: slide.background_color || '#000' }}>
                        <source src={getUploadUrl(slide.media.url)} type="video/mp4" />
                    </video>
                );
            }
            return null;
        }

        if (slide.media_type === 'image' && slide.media?.url) {
            if (isMobile) {
                // If enable_mobile_animation is true AND in portrait mode, show scrollable horizontal image
                if (slide.enable_mobile_animation && isPortrait) {
                    return (
                        <>
                            {/* <div
                                ref={stripRef}
                                data-strip-control="true"
                                style={{
                                    position: 'absolute',
                                    top: 100,
                                    left: 0,
                                    right: 0,
                                    zIndex: isZoomed ? -1 : 11,
                                    touchAction: 'none',
                                    mixBlendMode: 'difference',
                                    pointerEvents: isZoomed ? 'none' : 'auto',
                                }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        bottom: 12,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '60%',
                                        maxWidth: '70vw',
                                        height: 8,
                                        borderRadius: 8,
                                        background: '#fff',
                                        boxShadow: '0 10px 14px rgba(0,0,0,0.6)'
                                    }}
                                />
                            </div> */}
                            <TransformWrapper
                                initialScale={1}
                                minScale={1.0}
                                maxScale={4}
                                centerOnInit={false} // disable centering to avoid post-load jump
                                onTransformed={handleTransform}
                                doubleClick={{ disabled: false }}
                                wheel={{ disabled: false }}
                                panning={{ disabled: isZoomed ? false : true }}
                                velocityAnimation={{ disabled: true }}
                            >
                                <TransformComponent>
                                    <div
                                        ref={scrollContainerRef}
                                        onClick={(e) => {
                                            if (isZoomed) {
                                                e.stopPropagation();
                                            }
                                        }}
                                        onTouchStart={(e) => {
                                            if (isZoomed) {
                                                e.stopPropagation();
                                            }
                                        }}
                                        onTouchMove={(e) => {
                                            if (isZoomed) {
                                                e.stopPropagation();
                                            }
                                        }}
                                        onTouchEnd={(e) => {
                                            if (isZoomed) {
                                                e.stopPropagation();
                                            }
                                        }}
                                        style={{ width: '100%', height: '100%', overflowX: isZoomed ? 'hidden' : 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', userSelect: 'none', pointerEvents: 'auto', opacity: isAnimatingReady ? 1 : 0 }}
                                    >
                                        <div ref={imageWrapperRef} style={{ display: 'inline-block', height: '100%', whiteSpace: 'nowrap' }}>
                                            <ImageWithSkeleton
                                                src={getUploadUrl(slide.media.url)}
                                                alt={slide.alt_text || slide.title}
                                                loading={isActive ? 'eager' : 'lazy'}
                                                style={{ height: '100vh', width: 'auto', maxWidth: 'none', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
                                            />
                                        </div>
                                    </div>
                                </TransformComponent>
                            </TransformWrapper>
                        </>
                    );
                } else {
                    // If enable_mobile_animation is false OR in landscape mode, use fit_variant to determine objectFit
                    const fitVariant = slide.fit_variant || 'fill';
                    let objectFitStyle: React.CSSProperties = {};
                    if (fitVariant === 'horizontal') {
                        // Fit horizontally (left to right)
                        objectFitStyle = { width: '100%', height: 'auto', objectFit: 'contain' };
                    } else if (fitVariant === 'vertical') {
                        // Fit vertically (top to bottom)
                        objectFitStyle = { width: 'auto', height: '100%', objectFit: 'contain' };
                    } else if (fitVariant === 'contained') {
                        // Contained - image will NOT be cut from any sides
                        objectFitStyle = { width: '100%', height: '100%', objectFit: 'contain' };
                    } else {
                        // Fill full window
                        objectFitStyle = { width: '100%', height: '100%', objectFit: 'cover' };
                    }

                    return (
                        // <TransformWrapper
                        //     initialScale={1}
                        //     minScale={1.0}
                        //     maxScale={4}
                        //     centerOnInit={true}
                        //     onTransformed={handleTransform}
                        //     doubleClick={{ disabled: false }}
                        //     wheel={{ disabled: false }}
                        //     panning={{ disabled: isZoomed ? false : true }}
                        //     velocityAnimation={{ disabled: true }}
                        // >
                        //     <TransformComponent>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                    {slide.image_url ? (
                                        <div onClick={handleImageClick} style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'default' }}>
                                            <ImageWithSkeleton
                                                src={getUploadUrl(slide.media.url)}
                                                alt={slide.alt_text || slide.title}
                                                loading={isActive ? 'eager' : 'lazy'}
                                                style={{ ...objectFitStyle, pointerEvents: isZoomed ? 'auto' : 'none' }}
                                            />
                                        </div>
                                    ) : (
                                        <ImageWithSkeleton
                                            src={getUploadUrl(slide.media.url)}
                                            alt={slide.alt_text || slide.title}
                                            loading={isActive ? 'eager' : 'lazy'}
                                            style={{ ...objectFitStyle, pointerEvents: isZoomed ? 'auto' : 'none' }}
                                        />
                                    )}
                                </div>
                            // </TransformComponent>
                        // </TransformWrapper>
                    );
                }
            } else {
                // Desktop: also use fit_variant
                const fitVariant = slide.fit_variant || 'fill';
                let objectFitStyle: React.CSSProperties = {};
                if (fitVariant === 'horizontal') {
                    // Fit horizontally (left to right)
                    objectFitStyle = { width: '100%', height: 'auto', objectFit: 'contain' };
                } else if (fitVariant === 'vertical') {
                    // Fit vertically (top to bottom)
                    objectFitStyle = { width: 'auto', height: '100%', objectFit: 'contain' };
                } else if (fitVariant === 'contained') {
                    // Contained - image will NOT be cut from any sides
                    objectFitStyle = { width: '100%', height: '100%', objectFit: 'contain' };
                } else {
                    // Fill full window
                    objectFitStyle = { width: '100%', height: '100%', objectFit: 'cover' };
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        {slide.image_url ? (
                            <div onClick={handleImageClick} style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'default' }}>
                                <ImageWithSkeleton
                                    src={getUploadUrl(slide.media.url)}
                                    alt={slide.alt_text || slide.title}
                                    loading={isActive ? 'eager' : 'lazy'}
                                    style={{ ...objectFitStyle, pointerEvents: 'none' }}
                                />
                            </div>
                        ) : (
                            <ImageWithSkeleton
                                src={getUploadUrl(slide.media.url)}
                                alt={slide.alt_text || slide.title}
                                loading={isActive ? 'eager' : 'lazy'}
                                style={{ ...objectFitStyle, pointerEvents: 'none' }}
                            />
                        )}
                    </div>
                );
            }
        }
        return null;
    };

    return (
        <Box className="media-container" ref={mediaRef} sx={{ width: '100%', height: '100vh', position: 'relative', overflow: 'hidden', backgroundColor: slide.background_color || '#000' }}>
            {renderMedia()}

            {/* Navigation Zones - Invisible areas for swipe/navigation priority */}
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => {
                    setIsInNavigationZone(false);
                }}
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '200px',
                    height: '100%',
                    zIndex: 10,
                    pointerEvents: { xs: 'none', md: 'auto' } // Disable on mobile
                }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => {
                    setIsInNavigationZone(false);
                }}
                sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: '200px',
                    height: '100%',
                    zIndex: 10,
                    pointerEvents: { xs: 'none', md: 'auto' } // Disable on mobile
                }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => {
                    setIsInNavigationZone(false);
                }}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '200px',
                    zIndex: 10,
                    pointerEvents: { xs: 'none', md: 'auto' } // Disable on mobile
                }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => {
                    setIsInNavigationZone(false);
                }}
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '200px',
                    zIndex: 10,
                    pointerEvents: { xs: 'none', md: 'auto' } // Disable on mobile
                }}
            />

            {(isMobile ? (slide.mobile_text_content || '') : slide.text_content) && (
                <TextOverlay
                    text_content={isMobile ? (slide.mobile_text_content || '') : (slide.text_content || '')}
                    background_color={slide.background_color}
                    isActive={isActive}
                />
            )}
            
            <RotateDeviceIndicator show={slide.enable_rotate && isActive} />
        </Box>
    );
};

const getEmbedUrl = (url: string, platform?: string): string => {
    if (platform === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
        const videoId = extractYouTubeId(url);
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1`;
    } else if (platform === 'vimeo' || url.includes('vimeo.com')) {
        const videoId = extractVimeoId(url);
        return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1`;
    }
    return url;
};

const extractYouTubeId = (url: string): string => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : '';
};

const extractVimeoId = (url: string): string => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : '';
};

export default FullMediaSection;
